import type { Question, RoundMeta } from '@/domain/types';

/**
 * James's compatibility journey.
 *
 * ---------------------------------------------------------------------------
 * SHAPE
 * ---------------------------------------------------------------------------
 *
 * Seventeen questions, served as ten and then seven. The first round is the
 * one that has to stand on its own: someone who answers it and stops gets a
 * real result and a real invitation, so it has to contain everything James
 * would actually want to know. It does — every weight-5 question is in it, and
 * so are both non-negotiables.
 *
 * TEN IS NOT A TUNING KNOB. It is printed on the business cards — "ten
 * questions, about three minutes" — and somebody arrives here holding one. The
 * first round has to be the number in her hand, so `jamesRounds[0].size`,
 * `journeyInvitation` and the card all have to say the same thing, and a test
 * asserts the first two do. Changing it means reprinting the cards.
 *
 * The seven that follow are genuinely optional and genuinely worth doing: the
 * result page gives them their own group, so carrying on visibly adds
 * something rather than nudging an average nobody can see.
 *
 * ---------------------------------------------------------------------------
 * ORDER
 * ---------------------------------------------------------------------------
 *
 * Round one opens with a Saturday, a Friday night and the shape of a normal
 * week — concrete, instantly answerable, nobody has to be brave — then the
 * three foundations, and then humour. You earn the right to ask about
 * children; you do not open with it, and you do not end on it either. The last
 * thing round one asks about is what makes her laugh.
 *
 * Round two opens light for the same reason and ends on the cat.
 *
 * ---------------------------------------------------------------------------
 * SECTIONS
 * ---------------------------------------------------------------------------
 *
 * `core` (8) · `lifestyle` (6) · `preference` (3). The ratio is the point:
 * this is a set weighted towards what is hard to negotiate later, with the
 * specifics kept as the small tier they deserve to be.
 *
 * ---------------------------------------------------------------------------
 * THE CREATOR NOTES
 * ---------------------------------------------------------------------------
 *
 * Each one is reciprocal disclosure, not a defence of the score. They have
 * been cut back once already: several of them ended by heading off an
 * objection nobody had made — "I would never try to make it your hobby", "not
 * because I want to control someone" — which reads as somebody arguing with a
 * previous relationship. The specifics stayed; the reassurance went.
 *
 * ---------------------------------------------------------------------------
 * A NOTE ON TWO QUESTIONS THAT ARE NOT HERE
 * ---------------------------------------------------------------------------
 *
 * `togetherness` and `social-energy` were replaced, not deleted. James wrote
 * sharper versions of both — `independence` and `social-life` — and asking
 * both halves of the same axis reads to the person answering as a memory
 * test rather than a conversation. The creator notes and the carefully
 * written `notes.close` overrides came across intact; only the prompts and
 * the option wording are new.
 */

/**
 * Round copy, in order. Each round names its own length — see
 * `domain/rounds.ts`. Ten and seven are content decisions about where the
 * first commitment ends, not arithmetic about how seventeen divides.
 *
 * The ten is load-bearing: it is the number on the printed cards. See SHAPE.
 */
export const jamesRounds: RoundMeta[] = [
  {
    id: 'foundations',
    size: 10,
    label: 'The foundations',
    blurb: 'The things that are hardest to negotiate later.',
  },
  {
    id: 'a-little-deeper',
    size: 7,
    label: 'A little deeper',
    blurb:
      'How two weeks would fit alongside each other, and how each of us handles the harder days.',
  },
];

export const jamesQuestions: Question[] = [
  /* =========================================================================
     ROUND ONE - The foundations
     ========================================================================= */

  /**
   * The opener, and chosen for the job: concrete, instantly answerable, and
   * nobody has to be brave to answer it. Ticking four things off a list is
   * also an easier first move than committing to a single choice.
   *
   * Explicitly a day of her own, so it does not read as a first run at
   * `weekend-together` five questions later. That one asks what a weekend
   * looks like with somebody else in it, which is a different question, and
   * people answer the two very differently.
   */
  {
    id: 'good-saturday',
    kind: 'multi',
    weight: 1,
    section: 'lifestyle',
    theme: 'A good Saturday',
    prompt: 'A Saturday entirely your own, and the weather is suspiciously good. What goes in it?',
    help: 'Somewhere easy to start. Pick up to four - a day of your own rather than a weekend for two.',
    maxSelections: 4,
    options: [
      { id: 'slow-start', label: 'A slow start with good coffee' },
      { id: 'outdoors', label: 'A run, hike, walk or day outdoors' },
      { id: 'gym', label: 'A workout at the gym followed by a favourite treat' },
      { id: 'friends', label: 'Catching up with friends' },
      { id: 'bakery', label: 'Finding a great bakery or cafe' },
      { id: 'books-films-games', label: 'Books, films or games at home' },
      { id: 'projects', label: 'Making, fixing or learning something' },
      { id: 'night-out', label: 'A night out with friends and drinks' },
    ],
    creatorAnswer: {
      kind: 'multi',
      optionIds: ['outdoors', 'slow-start', 'gym', 'friends'],
    },
    creatorNote:
      'Probably a long run or a track somewhere. Then cooking, music, a game or a film. I like a day that has both movement and a sofa in it.',
    alignmentNote: 'Our version of a good Saturday already has plenty of overlap.',
    conversationPrompt:
      'We recharge a little differently. That usually means more options for the weekend, not a problem.',
  },

  /**
   * Two options are `exact`. A cosy night in and a relaxed social evening are
   * equally James's first pick, and the scoring should not make somebody pay
   * for choosing the other one.
   *
   * The first option is a genuine mismatch, and the help text no longer
   * pretends otherwise. "There is no wrong answer" was a kindness that made a
   * small liar of the question; "answer for a normal weekend" is the honest
   * version of the same reassurance.
   */
  {
    id: 'nights-out',
    kind: 'single',
    weight: 2,
    section: 'lifestyle',
    theme: 'Weekend rhythm',
    prompt: 'Your ideal Friday or Saturday night is usually…',
    help: 'Answer for a normal weekend rather than the best one you have had.',
    options: [
      {
        id: 'big-night',
        label: 'A big night out - bars, clubs, drinks, late night',
        alignment: 'different',
      },
      {
        id: 'lively',
        label: 'Dinner or drinks somewhere lively, but not too crazy',
        alignment: 'neutral',
      },
      { id: 'relaxed', label: 'Something social but fairly relaxed', alignment: 'exact' },
      { id: 'cosy', label: 'A cosy night at home', alignment: 'exact' },
      { id: 'depends', label: 'It really depends on the weekend', alignment: 'close' },
    ],
    creatorAnswer: { kind: 'single', optionId: 'relaxed' },
    creatorNote:
      'Dinner with a few people, or cooking for them, and a conversation I can actually hear. I hardly drink, so a two-in-the-morning finish has very little in it for me.',
    alignmentNote: 'We would pick a similar sort of evening without having to negotiate.',
    notes: {
      close:
        'Your weekends move around more than mine do. Easy enough to work with - it probably just means I am the one suggesting something.',
    },
    conversationPrompt:
      'Our ideal Friday night looks quite different. Worth knowing early, because it is most weekends rather than an occasional one.',
  },

  /**
   * Replaces `social-energy`. The old version asked where you come alive -
   * one-to-one, a small group, a big room - which is a question about setting.
   * This one asks about volume, which is the thing that actually has to match
   * week to week. The creator note is the old one, unchanged; it answers both.
   */
  {
    id: 'social-life',
    kind: 'single',
    weight: 2,
    section: 'lifestyle',
    theme: 'Social rhythm',
    prompt: 'How would you describe your ideal social life?',
    options: [
      {
        id: 'lots-of-events',
        label: 'Lots of events, parties and people around me',
        alignment: 'different',
      },
      {
        id: 'healthy-mix',
        label: 'A healthy mix of socialising and quiet time',
        alignment: 'exact',
      },
      {
        id: 'close-friends',
        label: 'Mostly smaller groups and close friends',
        alignment: 'exact',
      },
      { id: 'quiet', label: "I'm happiest living pretty quietly", alignment: 'exact' },
    ],
    creatorAnswer: { kind: 'single', optionId: 'close-friends' },
    creatorNote:
      'One-to-one or a handful of people around a table is where I am best. I can show up to the big event; I will just need a quiet day afterwards.',
    alignmentNote: 'We want a similar number of people in our week.',
    // No `notes.close` here either: three of the four options are `exact`,
    // deliberately, and none is `close`.
    conversationPrompt:
      'One of us gains energy from a full room the other needs to recover from. Usually manageable once neither takes it personally.',
  },

  {
    id: 'fitness',
    kind: 'single',
    weight: 3,
    section: 'lifestyle',
    theme: 'Health and fitness',
    prompt: 'How important are health and fitness in your everyday life?',
    help: 'Not a fitness test. It is really a question about how two weeks would fit together.',
    options: [
      {
        id: 'important',
        label: 'Important - I like staying active and healthy',
        alignment: 'exact',
      },
      {
        id: 'casual',
        label: "Nice to have, but I'm pretty casual about it",
        alignment: 'neutral',
      },
      { id: 'not-important', label: 'Not particularly important to me', alignment: 'different' },
    ],
    creatorAnswer: { kind: 'single', optionId: 'important' },
    creatorNote:
      "I run, I'm on a bouldering wall failing at the same problem for the third week running, and I'm in the gym most weeks. ",
    alignmentNote: 'Looking after ourselves is a similar sort of priority for both of us.',
    // No `notes.close`: there is no `close` option to reach it. The three
    // options run exact / neutral / different, so the two general strings say
    // everything this question can say.
    conversationPrompt:
      'Training takes up a real part of my week and matters much less in yours. Not a problem in itself - worth being honest about what a normal week actually looks like for each of us.',
  },

  /**
   * Deliberately about *us*, where `good-saturday` at the top of the round is
   * deliberately about *you*. The two questions cover similar ground and that
   * split is the whole reason both survive: one asks what you would do with a
   * free day of your own, this one asks what you would do with someone else in
   * it.
   *
   * Rewritten because the first version asked the wrong axis twice. "A party,
   * festival or big social event" was the only real point of difference on
   * offer, which made this a second run at `nights-out` and `social-life` — and
   * it isn't even a difference: James is perfectly happy at the occasional big
   * one, so scoring it as a divergence measured the wrong thing. "A mixture of
   * all of those" then quietly let everybody out of answering at all.
   *
   * The axis now is how a weekend gets *spent together*, which is the thing
   * this question was always meant to own. The mismatch at the bottom is a
   * weekend spent largely apart. That is close to `independence` on purpose:
   * that question asks for a principle and this one asks what Saturday looks
   * like, and people answer those two very differently.
   */
  {
    id: 'weekend-together',
    kind: 'single',
    weight: 2,
    section: 'lifestyle',
    theme: 'A weekend together',
    prompt: 'A free weekend, no plans, and someone you are seeing. What sounds best?',
    help: 'Think of an ordinary free weekend rather than a special one.',
    options: [
      {
        id: 'active',
        label: 'Out somewhere - a hike, a drive, somewhere neither of us has been',
        alignment: 'exact',
      },
      {
        id: 'people',
        label: 'Around other people - friends, family, or something big and social',
        alignment: 'neutral',
      },
      {
        id: 'wander',
        label: 'Brunch, wandering around town and seeing where the day takes us',
        alignment: 'close',
      },
      {
        id: 'one-of-each',
        label: 'One day out doing something, one day doing very little',
        alignment: 'exact',
      },
      {
        id: 'home',
        label: 'Cooking together, films, games and barely leaving the house',
        alignment: 'close',
      },
      {
        id: 'separate',
        label: 'Mostly our own plans, catching up in the evenings',
        alignment: 'different',
      },
    ],
    creatorAnswer: { kind: 'single', optionId: 'one-of-each' },
    creatorNote:
      'One of each, ideally. Out somewhere on the Saturday - a track, a hill, somewhere neither of us has been - and then nothing whatsoever on the Sunday except cooking and a film.',
    alignmentNote: 'A weekend together would more or less plan itself.',
    notes: {
      close:
        'We would each reach for a slightly different weekend, which mostly means taking turns and seeing more than either of us would alone.',
      neutral:
        'You would fill a free weekend with other people more often than I would. Good fun in reasonable doses - worth knowing it is not where I recover.',
    },
    conversationPrompt:
      'One of us pictures a free weekend spent together and the other pictures it spent side by side. Worth comparing notes - a weekend is a lot of the time two people actually share.',
  },

  /**
   * Replaces `togetherness`. Same axis, James's own wording, and the same
   * answer underneath. The `notes.close` override is carried over intact and
   * matters for the same reason it did before: "I need quite a lot of
   * independent time" is not the same answer as "lots of togetherness, and our
   * own lives too", and rounding one up to the other claims an agreement
   * nobody gave.
   */
  {
    id: 'independence',
    kind: 'single',
    weight: 3,
    section: 'core',
    theme: 'Independence',
    prompt: 'How much independence feels right in a relationship?',
    options: [
      { id: 'most-together', label: 'I love doing most things together', alignment: 'close' },
      {
        id: 'balanced',
        label: 'Lots of togetherness, but we should each have our own lives too',
        alignment: 'exact',
      },
      {
        id: 'quite-independent',
        label: 'I need quite a lot of independent time and separate interests',
        alignment: 'close',
      },
      {
        id: 'mostly-separate',
        label: 'I strongly prefer keeping most areas of life separate',
        alignment: 'different',
      },
    ],
    creatorAnswer: { kind: 'single', optionId: 'balanced' },
    creatorNote:
      "I want a real teammate and a genuinely shared life. Friends, interests and evenings of our own, and a strong 'us' we both keep choosing.",
    alignmentNote: 'We want closeness without asking either person to lose themselves.',
    notes: {
      close:
        'Neither of us wants a relationship that swallows two people whole. We would draw the line between together and independent in slightly different places, which is an easy thing to talk about and a hard thing to guess.',
    },
    conversationPrompt:
      'We picture the balance between independence and togetherness differently. That is worth making concrete.',
  },

  {
    id: 'intentions',
    kind: 'single',
    weight: 5,
    section: 'core',
    theme: 'Where this could go',
    prompt: 'If meeting the right person went unexpectedly well, what would you hope it could become?',
    help: 'No promise or timetable required - just the direction you are open to.',
    options: [
      {
        id: 'committed-future',
        label: 'A committed relationship, with marriage and family in the picture',
        alignment: 'exact',
      },
      {
        id: 'serious',
        label: 'A serious relationship, and we let the future unfold',
        alignment: 'close',
      },
      {
        id: 'meaningful',
        label: 'A meaningful connection without a fixed destination',
        alignment: 'neutral',
      },
      { id: 'casual', label: 'Something fun and casual', alignment: 'different' },
      { id: 'unsure', label: "Honestly, I'm not sure at the moment", alignment: 'neutral' },
    ],
    creatorAnswer: { kind: 'single', optionId: 'committed-future' },
    creatorNote:
      "I'm dating with the hope of building a loving, lasting relationship that could become marriage and a family. No certainty required on a first date - just the same direction.",
    alignmentNote: 'We are open to building the same kind of future.',
    notes: {
      close:
        'We both want something serious. I have a specific destination in mind - marriage and a family - and you have deliberately left that open. Same direction, different amount of certainty.',
    },
    conversationPrompt:
      'We may be dating with different destinations in mind. Better to be honest about that than hope it changes later.',
  },

  {
    id: 'children',
    kind: 'single',
    weight: 5,
    section: 'core',
    theme: 'Family',
    prompt: 'Looking ahead, does being a parent feel like part of the life you want?',
    help: 'Life is rarely tidy. This is about desire, not a perfect timetable.',
    options: [
      { id: 'want', label: 'Yes - I want children', alignment: 'exact' },
      {
        id: 'have-and-open',
        label: 'I have children, and I am open to growing or blending a family',
        alignment: 'close',
      },
      {
        id: 'have-complete',
        label: 'I have children, and I do not plan to have more',
        alignment: 'different',
      },
      { id: 'open', label: 'I could see it with the right person', alignment: 'close' },
      { id: 'unsure', label: "I'm genuinely unsure", alignment: 'neutral' },
      { id: 'no', label: "No - parenthood isn't the life I want", alignment: 'different' },
    ],
    nonNegotiable: {
      conflictOptionIds: ['no'],
      conflictSummary: "children - I want to be a parent, and you know that life isn't for you",
    },
    creatorAnswer: { kind: 'single', optionId: 'want' },
    creatorNote:
      'Yes. I want to be a father and build a family with my partner. Life rarely follows a neat plan - the shared wanting matters more to me than the timetable.',
    alignmentNote: 'We both want parenthood to be part of our future.',
    /**
     * The one that most needs saying precisely. "I could see it with the right
     * person" and "I have children and would grow the family" are both `close`,
     * and neither is "yes, I want children". Rounding either of them up to the
     * exact note would claim an agreement that was never given, on the single
     * question where getting it wrong costs the most.
     *
     * It used to end "close enough to be hopeful about, different enough to be
     * worth saying out loud" — which, under a heading that says *worth talking
     * about*, quietly took the decision back: the product deciding that the
     * uncertainty counted as sufficient agreement after all. It now names the
     * difference and hands it over, which is the whole point of the heading.
     *
     * This note is also why the question is `core` at weight 5: that pairing is
     * what puts a `close` answer under "worth talking about" rather than under
     * "where we align", and what stops the result reading as a yes. See
     * `groupFor` and `isUnresolvedCore` in `domain/scoring.ts`.
     */
    notes: {
      close:
        'I am certain I want children; you are open to it rather than set on it. Different levels of certainty, and important enough to discuss directly.',
    },
    conversationPrompt:
      'We are not in exactly the same place on children. That deserves a real conversation, not an assumption.',
  },

  {
    id: 'monogamy',
    kind: 'single',
    weight: 5,
    section: 'core',
    theme: 'Commitment',
    prompt: 'In a long-term relationship, what does commitment look like to you?',
    options: [
      {
        id: 'chosen-exclusive',
        label: 'Monogamous and chosen openly - just the two of us',
        alignment: 'exact',
      },
      {
        id: 'monogamous',
        label: 'Monogamous, without needing to make a big thing of the label',
        alignment: 'close',
      },
      { id: 'undecided', label: "I'm still working out what suits me", alignment: 'neutral' },
      {
        id: 'non-monogamous',
        label: 'An open or ethically non-monogamous relationship',
        alignment: 'different',
      },
    ],
    nonNegotiable: {
      conflictOptionIds: ['non-monogamous'],
      conflictSummary:
        "commitment - I'm looking for monogamy, and you're looking for an open relationship",
    },
    creatorAnswer: { kind: 'single', optionId: 'chosen-exclusive' },
    creatorNote:
      'Monogamy. Trust, loyalty and choosing each other are central to how I love, and I would rather make that promise explicit than leave either person guessing.',
    alignmentNote: 'We mean the same thing when we talk about commitment.',
    notes: {
      close:
        'We both mean monogamy. I would rather say it out loud; you would rather not make a thing of the label. A difference in style rather than in substance.',
    },
    conversationPrompt:
      'We may mean different things by commitment. That is important enough to understand early.',
  },

  /**
   * The last word of round one, and deliberately the lightest thing in it.
   * Three serious questions in a row is a lot to hand somebody and then send
   * to a result page; this is the exhale.
   */
  {
    id: 'humour',
    kind: 'multi',
    weight: 1,
    section: 'preference',
    theme: 'Playfulness',
    prompt: 'Lighter note to finish on: which kinds of humour tend to get you?',
    help: 'Pick up to three. Serious compatibility research ends here.',
    maxSelections: 3,
    options: [
      { id: 'dry', label: 'Dry and deadpan' },
      { id: 'warm', label: 'Warm, affectionate teasing' },
      { id: 'silly', label: 'Silly or completely absurd' },
      { id: 'wordplay', label: 'Clever wordplay and quick observations' },
      { id: 'dark', label: 'A little dark, honestly' },
      { id: 'chaotic', label: 'Chaotic and impossible to explain afterwards' },
    ],
    creatorAnswer: { kind: 'multi', optionIds: ['dry', 'wordplay', 'silly'] },
    creatorNote:
      'Dry and self-aware, clever wordplay, and the completely absurd. I love stand-up and I laugh easily.',
    alignmentNote: 'We are likely to make each other laugh, which is not a small thing.',
    conversationPrompt:
      'Our comic timing is different. One good dinner should reveal whether that matters at all.',
  },

  /* =========================================================================
     ROUND TWO - A little deeper

     Not "the everyday, lighter mostly", which is what this round used to be
     called and was never quite true of it: affection, care and repair are in
     here, and calling them light undersells what somebody is agreeing to
     answer.
     ========================================================================= */

  /**
   * Round two opens the way round one did: with something nobody has to think
   * hard about, and something concrete enough to answer without deciding
   * anything about themselves first.
   */
  {
    id: 'diet',
    kind: 'single',
    weight: 2,
    section: 'preference',
    theme: 'Food',
    prompt: 'When it comes to food and dietary choices…',
    help: 'Here because it quietly shapes almost every meal two people eat together.',
    options: [
      {
        id: 'everything',
        label: 'I eat pretty much everything, including meat',
        alignment: 'exact',
      },
      {
        id: 'veg-flexible',
        label: "I'm vegetarian or vegan, and completely comfortable dating someone who eats meat",
        alignment: 'close',
      },
      {
        id: 'veg-shared',
        label: "I'm vegetarian or vegan, and would prefer my partner to be as well",
        alignment: 'different',
      },
      {
        id: 'other',
        label: 'I have other dietary needs or preferences that matter in a relationship',
        alignment: 'neutral',
      },
    ],
    creatorAnswer: { kind: 'single', optionId: 'everything' },
    creatorNote:
      'I eat pretty much everything, and I cook properly most nights - the cooking is the part I would actually miss. If you eat differently to me I will happily learn to cook around it.',
    alignmentNote: 'We would be easy to feed at the same table.',
    notes: {
      close:
        'We eat differently, and you have said that is fine by you. Fine by me too - I like cooking enough to enjoy the problem.',
    },
    conversationPrompt:
      'Food is a small thing every single day, which is exactly what makes it worth naming. Much better said plainly than discovered over a menu.',
  },

  {
    id: 'substances',
    kind: 'single',
    weight: 3,
    section: 'lifestyle',
    theme: 'Everyday lifestyle',
    prompt: 'Where do drinking, smoking or vaping fit into your life?',
    help: 'This is about day-to-day fit, not a moral score.',
    options: [
      { id: 'none', label: "I don't drink, smoke or vape at all", alignment: 'close' },
      {
        id: 'rarely',
        label: 'I rarely drink - the odd one on a special occasion - and I do not smoke or vape',
        alignment: 'exact',
      },
      { id: 'social-drink', label: 'I drink socially, but do not smoke or vape', alignment: 'close' },
      { id: 'regular-drink', label: 'Drinking is a regular part of how I socialise', alignment: 'neutral' },
      { id: 'smoke', label: 'I smoke or vape', alignment: 'different' },
    ],
    creatorAnswer: { kind: 'single', optionId: 'rarely' },
    creatorNote:
      "I don't drink regularly, and I don't smoke or vape. On a proper occasion a good whiskey or a glass of red is a pleasure - it is just not part of an ordinary week. Coffee, walks, dinner and a conversation we can actually hear are.",
    alignmentNote: 'Our everyday habits and preferred ways of socialising fit easily together.',
    notes: {
      close:
        'We are not in quite the same place on drinking, though neither of us is far off. Mine is a good whiskey or a glass of red on a proper occasion rather than anything weekly. Genuinely easy either way.',
    },
    conversationPrompt:
      'Our habits differ a little. What matters is whether both people can live normally without pressure to change.',
  },

  {
    id: 'affection',
    kind: 'single',
    weight: 3,
    section: 'core',
    theme: 'Affection',
    prompt: 'How affectionate are you naturally?',
    help: 'People differ a lot here, and it rarely gets discussed until someone feels short of it.',
    options: [
      {
        id: 'very',
        label: 'Very - touch, cuddling and affection are a big deal to me',
        alignment: 'exact',
      },
      { id: 'fairly', label: 'Fairly affectionate', alignment: 'close' },
      {
        id: 'some',
        label: "Affection is nice, but I don't need much of it",
        alignment: 'neutral',
      },
      {
        id: 'not-much',
        label: "I'm not particularly physically affectionate",
        alignment: 'different',
      },
    ],
    creatorAnswer: { kind: 'single', optionId: 'very' },
    creatorNote:
      'Very. A hand on your back on the way past, sitting closer than strictly necessary, and saying the affectionate thing out loud rather than assuming it is obvious.',
    alignmentNote: 'Affection is a big deal to both of us, which is easier than it sounds.',
    notes: {
      close:
        'We are both affectionate; I am probably the more demonstrative of the two. Easy to meet in the middle once it is said rather than guessed at.',
    },
    conversationPrompt:
      'Physical affection matters more to one of us than the other. A genuinely common difference, and one that only goes badly when nobody names it.',
  },

  {
    id: 'relationship-adds',
    kind: 'multi',
    weight: 2,
    section: 'core',
    theme: 'What love adds',
    prompt: 'What would you most want a great relationship to add to your life?',
    help: 'Choose the things that matter most to you - up to five.',
    maxSelections: 5,
    options: [
      { id: 'team', label: 'A best friend and real teammate' },
      { id: 'warmth', label: 'Affection, warmth and emotional closeness' },
      { id: 'home-family', label: 'A home and family to build together' },
      { id: 'adventure', label: 'Shared adventures and good stories' },
      { id: 'growth', label: 'Someone to grow alongside' },
      { id: 'calm', label: 'More peace and steadiness' },
      { id: 'play', label: 'More laughter and everyday fun' },
    ],
    creatorAnswer: {
      kind: 'multi',
      optionIds: ['team', 'warmth', 'home-family', 'growth', 'play'],
    },
    creatorNote:
      'A loving teammate, a warm home, a family, and plenty of laughing while we build it. I want a relationship to make two already-full lives richer.',
    alignmentNote: 'We want a relationship to add the same things to our lives.',
    conversationPrompt:
      'We put the emphasis in different places. That is useful to know, and probably an interesting conversation.',
  },

  {
    id: 'care',
    kind: 'multi',
    weight: 3,
    section: 'core',
    theme: 'Emotional warmth',
    prompt: 'When someone you love has had a genuinely hard day, what comes naturally to you?',
    help: 'Pick up to three. Different kinds of care can all be good care.',
    maxSelections: 3,
    options: [
      { id: 'listen', label: 'Listen and let them feel understood' },
      { id: 'affection', label: 'Offer affection and reassurance' },
      { id: 'practical', label: 'Make life easier in a practical way' },
      { id: 'space-check-in', label: 'Give them space, then check back in' },
      { id: 'lightness', label: 'Bring food, humour or a useful distraction' },
    ],
    creatorAnswer: { kind: 'multi', optionIds: ['listen', 'affection', 'practical'] },
    creatorNote:
      "My engineer reflex is to solve the problem. I'm getting better at asking first whether you want comfort, a plan, or just someone beside you.",
    alignmentNote: 'The way we naturally show care is likely to feel familiar to both of us.',
    conversationPrompt:
      'We may give and recognise care differently. That is usually easy to learn once it is spoken about.',
  },

  /**
   * Rewritten after a review pointed out - correctly - that the previous
   * version measured what people know they are supposed to say rather than
   * what they would do. It offered "give it time and hope it fades" as a
   * scored-down option, which nobody hoping to be liked was ever going to
   * pick, and asked what "healthy repair" looks like, which announces the
   * right answer in the prompt.
   *
   * It is now a question about timing, not virtue: every option is one a
   * decent person could honestly choose, and the weight has come down from 4
   * to 3 because a preference about pacing should not pull on the result as
   * hard as a foundation does.
   */
  {
    id: 'conflict',
    kind: 'scenario',
    weight: 3,
    section: 'core',
    theme: 'After a disagreement',
    scenario: 'Something one of us said has landed badly, and we are both a bit prickly about it.',
    prompt: 'What actually helps you?',
    help: 'There is no right answer here - people just run on different clocks.',
    options: [
      {
        id: 'pause-and-return',
        label: 'A short breather, then come back and talk plainly',
        alignment: 'exact',
      },
      { id: 'talk-now', label: 'Talk it through now, while it is fresh', alignment: 'close' },
      {
        id: 'sleep-then-return',
        label: 'Sleep on it, then pick it up tomorrow',
        alignment: 'close',
      },
      {
        id: 'write-it',
        label: 'Put it in writing first - I am clearer that way',
        alignment: 'close',
      },
      {
        id: 'need-space',
        label: 'A lot of space, and I may not know when I will be ready',
        alignment: 'neutral',
      },
    ],
    creatorAnswer: { kind: 'single', optionId: 'pause-and-return' },
    creatorNote:
      'I usually need twenty minutes and a walk before I am any use to anybody, and then I would rather sort it than let it sit. The thing I have had to learn is saying when I will be back - I used to just go quiet and assume that was obvious.',
    alignmentNote: 'We come back from a disagreement at about the same speed.',
    notes: {
      close:
        'We would each want a different amount of time before talking, which is a very easy thing to work around once it has been said out loud.',
    },
    conversationPrompt:
      'We run on quite different clocks after a disagreement. Not a problem in itself, as long as neither of us is left guessing.',
  },

  /**
   * Added in place of the old accountability question, which asked people to
   * describe how well they apologise - a question almost nobody answers
   * unflatteringly, and the third pass over ground the values section already
   * covers twice.
   *
   * This one is deliberately ordinary and impossible to answer strategically.
   * It also happens to be a real gate: there is a cat in the third photograph
   * and he is not going anywhere. A fine last word for the whole set.
   */
  {
    id: 'home-and-animals',
    kind: 'single',
    weight: 3,
    section: 'preference',
    theme: 'Home and animals',
    prompt: 'I have the sweetest pet cat. Where do you land on furry friends at home?',
    help: 'Asked because it is the sort of thing people discover far too late.',
    options: [
      { id: 'delighted', label: 'Delighted! A house needs an animal in it', alignment: 'exact' },
      { id: 'dog-person', label: "Yes, though I'd have gone for a dog", alignment: 'close' },
      {
        id: 'happy-enough',
        label: "Happy to live with one, wouldn't have chosen it myself",
        alignment: 'close',
      },
      { id: 'rather-not', label: "I'd rather my own home stayed animal-free", alignment: 'neutral' },
      { id: 'allergic', label: 'Allergies make it a genuine problem', alignment: 'different' },
    ],
    creatorAnswer: { kind: 'single', optionId: 'delighted' },
    creatorNote:
      'I grew up with cats, dogs and even birds. There is a dog in the plan somewhere, once the timing is right.',
    alignmentNote: 'Neither of us wants a home without an animal in it.',
    notes: {
      close:
        'You are happy enough to live with an animal, even if a cat would not have been your pick. That works fine from here.',
    },
    conversationPrompt:
      'I come with a cat, and that is not something I can trade away. Much better to know that now than three dates in.',
  },
];
